import { motion } from "framer-motion";
import { 
  Rocket, 
  BarChart3, 
  PieChart, 
  TrendingUp,
  Compass,
  ArrowRight
} from "lucide-react";

const iconMap = {
  rocket: Rocket,
  chart: BarChart3,
  analytics: PieChart,
  growth: TrendingUp,
  consulting: Compass,
};

const ServiceCard = ({ service, index }) => {
  const Icon = iconMap[service.icon] || BarChart3;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="service-card group"
      data-testid={`service-card-${service.id}`}
    >
      <div className="icon-wrapper">
        <Icon className="w-6 h-6 text-[#007AFF] group-hover:text-[#00C853] transition-colors" />
      </div>
      
      <h3 className="text-xl md:text-2xl font-bold mb-3">{service.title}</h3>
      
      <p className="text-[#A1A1AA] mb-6">{service.description}</p>
      
      <ul className="space-y-3">
        {service.features?.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-3 text-[#A1A1AA] text-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-[#007AFF] to-[#00C853] mt-1.5 flex-shrink-0"></span>
            {feature}
          </li>
        ))}
      </ul>
    </motion.div>
  );
};

const Services = ({ services }) => {
  const scrollToContact = () => {
    const element = document.querySelector("#contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section id="services" className="py-20 md:py-28 bg-[#1A1F26]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-sm text-[#007AFF] font-medium uppercase tracking-wider">Services</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mt-4 mb-6">
            How We Can Help Grow Your <br />
            <span className="gradient-text">E-Commerce & App</span>
          </h2>
          <p className="text-[#A1A1AA] max-w-2xl mx-auto text-lg">
            Comprehensive E-commerce & app marketing services designed to drive user acquisition, 
            maximize retention, and scale revenue profitably.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          {services.map((service, index) => (
            <ServiceCard key={service.id} service={service} index={index} />
          ))}
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="cta-section"
          data-testid="services-cta"
        >
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Ready to scale your app & e-commerce?
          </h3>
          <p className="text-[#A1A1AA] mb-8 max-w-xl mx-auto">
            Let's discuss your growth goals and create a customized strategy for your business.
          </p>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={scrollToContact}
            className="gradient-btn px-8 py-4 text-lg font-semibold inline-flex items-center gap-2"
            data-testid="services-cta-button"
          >
            Schedule Free Consultation
            <ArrowRight className="w-5 h-5" />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
